<template>
  <div class="wc-item-group-selector" ref="selectorRoot">
    <button
      type="button"
      class="wc-igs-trigger"
      @click="toggleDropdown"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-controls="isOpen ? 'wc-igs-panel' : null"
      :aria-label="$t('categoriesMenu')"
    >
      <span class="wc-igs-trigger__label">{{ triggerLabel }}</span>
      <WCSvgIcon name="chevron-down" class="wc-igs-trigger__chevron" />
    </button>

    <ul id="wc-igs-panel" v-if="isOpen" class="wc-igs-panel" :aria-label="$t('categoriesMenu')">
      <li class="wc-igs-item">
        <button
          type="button"
          class="wc-igs-item__action"
          :aria-current="selectedId === allType.id ? 'true' : null"
          @click="selectItem(allType.id)"
        >
          <span class="wc-igs-item__check" aria-hidden="true">
            <WCSvgIcon
              v-if="selectedId === allType.id"
              name="tick-icon"
              class="wc-igs-item__tick"
            />
          </span>
          <span class="wc-igs-item__label">{{ $t('allCategories') }}</span>
        </button>
      </li>

      <template v-if="sortedItemGroups">
        <li v-for="itemGroup in sortedItemGroups" :key="itemGroup.id" class="wc-igs-item">
          <button
            type="button"
            class="wc-igs-item__action"
            :aria-current="selectedId === itemGroup.id ? 'true' : null"
            @click="selectItem(itemGroup.id)"
          >
            <span class="wc-igs-item__check" aria-hidden="true">
              <WCSvgIcon
                v-if="selectedId === itemGroup.id"
                name="tick-icon"
                class="wc-igs-item__tick"
              />
            </span>
            <span class="wc-igs-item__label">{{ itemGroup.name }}</span>
          </button>
        </li>
      </template>

      <li v-if="viewAll" class="wc-igs-item wc-igs-item--view-all">
        <span class="wc-igs-item__check" aria-hidden="true"></span>
        <span class="wc-igs-item__label">{{ $t('viewAll') }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
import { dynamicSort } from '@/utils';
import WCSvgIcon from '@/components/WCSvgIcon/WCSvgIcon.vue';
import emitter from '@/eventBus';
import { SIDE_MENU_EVENTS } from '@/constants/EventConstants';

const ALL_LABEL = 'All';
const ITEM_GROUP_SORT_KEY = 'name';

export default {
  name: 'WCItemGroupSelector',

  emits: ['change'],

  components: { WCSvgIcon },

  props: {
    itemGroups: {
      required: true,
      type: Array,
    },
    viewAll: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      allType: { id: '-1', name: 'all_type' },
      selectedId: '-1',
      isOpen: false,
    };
  },
  created() {
    this.selectedId = this.allType.id;
    this.change();
  },
  mounted() {
    this._clickOutsideHandler = e => {
      if (this.$refs.selectorRoot && !this.$refs.selectorRoot.contains(e.target)) {
        this.isOpen = false;
      }
    };
    document.addEventListener('click', this._clickOutsideHandler);
    this._sideMenuCloseHandler = () => {
      this.isOpen = false;
    };
    emitter.$on(SIDE_MENU_EVENTS.OPEN, this._sideMenuCloseHandler);
    emitter.$on(SIDE_MENU_EVENTS.TOGGLE, this._sideMenuCloseHandler);
  },

  unmounted() {
    document.removeEventListener('click', this._clickOutsideHandler);
    emitter.$off(SIDE_MENU_EVENTS.OPEN, this._sideMenuCloseHandler);
    emitter.$off(SIDE_MENU_EVENTS.TOGGLE, this._sideMenuCloseHandler);
  },

  computed: {
    sortedItemGroups() {
      return dynamicSort(this.itemGroups, ITEM_GROUP_SORT_KEY);
    },
    triggerLabel() {
      if (this.selectedId === this.allType.id) {
        return ALL_LABEL;
      }
      const found = (this.sortedItemGroups || []).find(g => g && g.id === this.selectedId);
      return found ? found.name : ALL_LABEL;
    },
  },

  methods: {
    closeDropdown() {
      this.isOpen = false;
    },

    toggleDropdown() {
      this.isOpen = !this.isOpen;
    },

    selectItem(id) {
      this.selectedId = id;
      this.change();
      this.closeDropdown();
    },

    change() {
      this.$emit('change', this.selectedId);
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-item-group-selector {
  position: relative;
  display: inline-flex;
  align-items: stretch;
  height: 100%;
}

.wc-igs-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0 0.75rem;
  height: 100%;
  background: var(--white);
  border: 0;
  border-right: 0.5px solid var(--dark-lighter-5);
  border-radius: 0;
  font-size: $font-size-base;
  font-weight: 500;
  color: var(--dark-lighter-5);
  white-space: nowrap;
  cursor: pointer;

  &:focus {
    outline: none;
    box-shadow: none;
  }

  &__chevron {
    display: inline-flex;
    align-items: center;
    color: var(--gray-700, $gray-700);

    :deep(svg) {
      width: 0.8rem;
      height: 0.8rem;
    }
  }
}

.wc-igs-panel {
  position: absolute;
  top: calc(100%);
  left: 0;
  z-index: 10000;
  width: var(--wc-search-bar-width, max-content);
  min-width: max-content;
  background-color: var(--white);
  border: 0.5px solid var(--dark-lighter-5);
  border-radius: 0 0 0.1875rem 0.1875rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  list-style: none;
  margin: 0;
  padding: 0.25rem 0;
}

.wc-igs-item {
  display: block;
  font-size: $font-size-base;
  color: var(--body-color, $body-color);
  position: relative;

  &:not(:first-child)::before {
    content: '';
    position: absolute;
    inset: 0 1rem auto;
    border-top: 1px solid var(--dark-lighter-5);
  }

  &__action {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.5rem 1rem;
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    text-align: left;

    &:focus {
      outline: none;
      background-color: var(--primary-lighter-9, var(--primary));
    }

    &:hover {
      background-color: var(--primary-lighter-9, var(--primary));
    }
  }

  &__check {
    width: 1rem;
    text-align: center;
    font-weight: 700;
    color: var(--primary);
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__label {
    flex: 1;
    white-space: nowrap;
  }

  &--view-all .wc-igs-item__label {
    color: var(--primary);
    font-weight: 500;
  }
}
</style>
